/*
  Kiwi Ears · frequency-response visualizer (Figma 221:530) — D3 v7 + GSAP.
  - Main curve: custom.product_sound_graph (text) via #pk-fr-text, else pdp.fr_curve (JSON) via #pk-fr-data,
    falling back to the bundled Quintet curve.
  - Compare: searchable picker (predictive search API) → fetches the chosen product's
    curve from /products/<handle>?view=frdata → draws it as the yellow dotted line.
  - Palette is Figma 189:8257 (Rene 2026-08-19): purple radial plot field, spring-green
    curve + glow over a green→teal area fill, #FFF200 dotted compare curve.
  - Plot keeps the Figma aspect ratio (584/1416) at every width — curve steepness is
    the information; never flatten on mobile (Rene 2026-08-14).
*/
(function () {
  var mount = document.getElementById('pk-fr-chart');
  if (!mount || !window.d3) return;
  var d3 = window.d3;

  var GREEN = '#00FF99', GREEN_GLOW = '#00E58A', COMPARE_YELLOW = '#FFF200', SWATCH_IDLE = '#E2E2E2';
  /* plot field: Figma's radial, brightest toward the top-right, sinking to near-black navy */
  var FIELD_STOPS = [[0, '#5A4DCB'], [0.25, '#4A40A5'], [0.5, '#393280'], [0.75, '#29255A'], [1, '#181734']];
  var AREA_TOP = '#00E58A', AREA_BOTTOM = '#11FFBF';
  var FONT_SANS = "'Robout',sans-serif";
  var FONT_MONO = "'JetBrains Mono',ui-monospace,monospace";


  function readCurve(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      var pts = JSON.parse(el.textContent);
      if (Array.isArray(pts) && pts.length > 2 && Array.isArray(pts[0])) return pts;
    } catch (e) {}
    return null;
  }
  /* text curve from custom.product_sound_graph — "hz,db" per line; commas,
     tabs, semicolons or spaces all work, header rows and #/// comments are
     skipped (Rene 2026-08-18). Falls back to the pdp.fr_curve JSON. */
  function parseCurveText(raw) {
    if (typeof raw !== 'string') return null;
    var out = [];
    raw.split(/[\r\n]+/).forEach(function (line) {
      line = line.trim();
      if (!line || /^[#;*/]/.test(line)) return;
      var cols = line.split(/[\s,;\t]+/).filter(Boolean);
      if (cols.length < 2) return;
      var hz = parseFloat(cols[0]), db = parseFloat(cols[1]);
      if (isFinite(hz) && isFinite(db) && hz > 0) out.push([hz, db]);
    });
    return out.length > 2 ? out : null;
  }
  function readCurveText(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    var raw = el.textContent;
    try { var s = JSON.parse(raw); if (typeof s === 'string') raw = s; } catch (e) {}
    return parseCurveText(raw);
  }
  /* No measured curve for THIS product = no chart (Rene 2026-09-11). The
     bundled fallback curve was Quintet's own measurement, so using it as a
     stand-in printed one product's data on another's page; the constant is
     gone. The section already hides
     the card when both metafields are empty; this is the second line of
     defence — unparseable data lands here too, and inventing a curve is worse
     than showing none. */
  /* ---- level-match every curve at 1 kHz (Rene 2026-09-14) ----------------
     The y axis is a FIXED 40–81 dB window and the curves were plotted at
     whatever absolute level the coupler happened to capture, so framing was
     pot luck: Stardust peaks at 81.0 dB and sat welded to the top border with
     0.0 dB of headroom, while Halcyon's 69.7 dB peak floated 11 dB lower. It
     also made the compare feature lie — two IEMs an octave apart in capture
     gain looked like they differed in tuning.

     Every curve is now shifted so it reads NORM_TARGET at 1 kHz, which is the
     usual way FR graphs are stacked (the measurements page does the same at
     60 dB). 67 dB is the middle of the 65–70 band Rene asked for, and it
     lands both current measurements inside the window with ~6 dB of air at
     the top: Stardust 47.5–74.7, Halcyon 55.1–75.1. The shift is uniform, so
     the SHAPE — the only thing this graph is claiming — is untouched. */
  /* ---- keep curves inside the plotted span -------------------------------
     The x axis is a LOG domain of 20 Hz – 20 kHz and d3 log scales do not
     clamp. These exports are REW "Hz dB phase" files that sweep well past the
     audible band — Halcyon's starts at 0.37 Hz — so the line ran off the plot
     and dragged 28 dB of sub-audible rubbish through the card. Same trim the
     measurements page got on 2026-09-03: drop what is outside, interpolate the
     boundary sample in log-f so the curve still reaches both edges exactly. */
  var F_MIN = 20, F_MAX = 20000;
  function interpLog(a, b, f) {
    var t = (Math.log(f) - Math.log(a[0])) / (Math.log(b[0]) - Math.log(a[0]));
    return isFinite(t) ? a[1] + t * (b[1] - a[1]) : a[1];
  }
  function clampCurve(pts) {
    if (!Array.isArray(pts)) return null;
    var src = pts.filter(function (d) {
      return Array.isArray(d) && isFinite(d[0]) && isFinite(d[1]) && d[0] > 0;
    }).sort(function (a, b) { return a[0] - b[0]; });
    var out = [];
    for (var i = 0; i < src.length; i++) {
      var pt = src[i], prev = src[i - 1];
      if (pt[0] < F_MIN) continue;
      if (prev && prev[0] < F_MIN && pt[0] > F_MIN) out.push([F_MIN, interpLog(prev, pt, F_MIN)]);
      if (pt[0] > F_MAX) {
        if (prev && prev[0] < F_MAX) out.push([F_MAX, interpLog(prev, pt, F_MAX)]);
        break;
      }
      out.push(pt);
    }
    return out.length > 2 ? out : null;
  }

  /* ---- soften the trace (Rene 2026-09-17) --------------------------------
     "can we soften the curves so we dont show as many micro adjustments".
     These are raw REW exports at 957 points across 20 Hz - 20 kHz, drawn into
     a ~1180px card: most of the wiggle is measurement noise finer than the
     pixel it lands on, not tuning anyone can hear.

     This is fractional-octave smoothing, the standard way measurement traces
     are presented: each output sample is the average of every input sample
     within +/-1/(2N) octave of it, so the window widens with frequency exactly
     as hearing does. A fixed-width average would flatten the treble and leave
     the bass ragged. The curve is then resampled onto a log grid, which is
     also what stops a 957-point path being re-drawn on every resize.

     SMOOTH_OCTAVE = 6 is 1/6-octave: broad features (the bass shelf, the ear-
     gain peak, the treble roll) survive untouched, the sub-pixel chatter does
     not. The shape is what this graph claims, and smoothing does not move it —
     but it IS a presentation choice, so the card says so in its caption. */
  var SMOOTH_OCTAVE = 6, SMOOTH_POINTS = 260;
  function smooth(pts) {
    if (!Array.isArray(pts) || pts.length < 8 || !SMOOTH_OCTAVE) return pts;
    var lo = pts[0][0], hi = pts[pts.length - 1][0];
    if (!(hi > lo)) return pts;
    var half = Math.pow(2, 1 / (2 * SMOOTH_OCTAVE));   /* window edge ratio */
    var out = [], i, k;
    for (i = 0; i < SMOOTH_POINTS; i++) {
      var f = lo * Math.pow(hi / lo, i / (SMOOTH_POINTS - 1));
      var a = f / half, b = f * half, sum = 0, n = 0;
      for (k = 0; k < pts.length; k++) {
        var pf = pts[k][0];
        if (pf < a) continue;
        if (pf > b) break;
        sum += pts[k][1]; n++;
      }
      /* a window narrower than the sample spacing (the very bottom of the
         sweep) falls back to the interpolated value rather than to nothing */
      out.push([f, n ? sum / n : levelAt(pts, f)]);
    }
    return out;
  }

  var NORM_TARGET = 67;
  var normBisect = d3.bisector(function (d) { return d[0]; }).left;
  function levelAt(pts, f) {
    if (!pts || pts.length < 2) return null;
    if (f <= pts[0][0]) return pts[0][1];
    if (f >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
    var i = Math.min(Math.max(normBisect(pts, f), 1), pts.length - 1);
    var a = pts[i - 1], b = pts[i];
    var t = (Math.log(f) - Math.log(a[0])) / (Math.log(b[0]) - Math.log(a[0]));
    return isFinite(t) ? a[1] + t * (b[1] - a[1]) : a[1];
  }
  function normalise(pts) {
    var ref = levelAt(pts, 1000);
    if (ref == null || !isFinite(ref)) return pts;
    var shift = NORM_TARGET - ref;
    return pts.map(function (d) { return [d[0], d[1] + shift]; });
  }

  var MAIN = readCurveText('pk-fr-text') || readCurve('pk-fr-data');
  if (!MAIN) {
    var card = document.getElementById('pk-fr-card');
    if (card) card.hidden = true; else mount.hidden = true;
    return;
  }
  MAIN = clampCurve(MAIN) || MAIN;
  MAIN = smooth(MAIN);
  MAIN = normalise(MAIN);

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var drawn = false;
  var compare = null; /* { name, pts } */
  var svg, x, y, plotW, plotH, quintetPath, comparePath, quintetArea, compareDash, bandAway;

  /* Named frequency bands (Rene 2026-08-19) — dividers + labels across the top
     of the plot, each explaining what it does on hover/tap. */
  var BANDS = [
    { name: 'Sub-bass',   short: 'Sub',      from: 20,    to: 60,    range: '20–60 Hz',      blurb: 'Deep rumble, physical impact, electronic bass, kick-drum extension.' },
    { name: 'Mid-bass',   short: 'Bass',     from: 60,    to: 250,   range: '60–250 Hz',     blurb: 'Bass punch, warmth, bass-guitar body, kick-drum weight.' },
    { name: 'Lower mids', short: 'Lo mids',  from: 250,   to: 1000,  range: '250 Hz – 1 kHz', blurb: 'Instrument body, vocal warmth, thickness, fullness.' },
    { name: 'Upper mids', short: 'Hi mids',  from: 1000,  to: 4000,  range: '1–4 kHz',       blurb: 'Vocal presence, clarity, guitars, attack, intelligibility.' },
    { name: 'Treble',     short: 'Treble',   from: 4000,  to: 10000, range: '4–10 kHz',      blurb: 'Detail, brightness, cymbal energy, consonants, sharpness.' },
    { name: 'Air',        short: 'Air',      from: 10000, to: 20000, range: '10–20 kHz',     blurb: 'Openness, shimmer, spaciousness, upper-harmonic extension.' }
  ];

  var X_TICKS = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
  /* phones drop 50/200/5k only; 500 and 10k stay — the mid and top bands are
     unreadable without them (Rene 2026-08-19) */
  var X_TICKS_SMALL = [20, 100, 500, 1000, 2000, 10000, 20000];
  var MAJOR = [50, 100, 200, 2000, 5000, 10000];
  function fmtHz(v, small) {
    /* phones drop the Hz suffixes — "10k"/"20k" is the only way both end labels fit */
    if (small) return v >= 1000 ? (v / 1000) + 'k' : '' + v;
    return v >= 1000 ? (v / 1000) + 'k' + (v === 20000 ? 'Hz' : '') : v + (v === 20 ? 'Hz' : '');
  }
  function lineGen() {
    return d3.line()
      .x(function (d) { return x(d[0]); })
      .y(function (d) { return y(d[1]); })
      .curve(d3.curveMonotoneX);
  }
  function areaGen() {
    return d3.area()
      .x(function (d) { return x(d[0]); })
      .y0(function () { return plotH; })
      .y1(function (d) { return y(d[1]); })
      .curve(d3.curveMonotoneX);
  }

  /* Audio-graph rule: fixed plot aspect (Figma 584/1416) at every width. */
  var PLOT_ASPECT = 584 / 1416;
  function build() {
    mount.innerHTML = '';
    var W = mount.clientWidth;
    var isSmall = W < 720;
    /* Under 1024px the mount bleeds past the card's 16px padding (see the
       -mx-4 in pk-tuning.liquid), so the axis gutters absorb that inset
       themselves and the plot keeps the width it gained (Rene 2026-08-19). */
    var bleed = Math.max(0, -(parseFloat(window.getComputedStyle(mount).marginLeft) || 0));
    var m = {
      top: 10,
      right: (isSmall ? 8 : 10 + bleed),
      bottom: isSmall ? 26 : 34,
      /* floor at 34 so the dB labels never clip if the bleed class is absent */
      left: isSmall ? Math.max(34, 30 + bleed) : 52 + bleed
    };
    plotW = W - m.left - m.right;
    plotH = Math.round(plotW * PLOT_ASPECT);
    var H = plotH + m.top + m.bottom;

    x = d3.scaleLog().domain([20, 20000]).range([0, plotW]);
    y = d3.scaleLinear().domain([40, 81]).range([plotH, 0]);

    svg = d3.select(mount).append('svg')
      .attr('width', W).attr('height', H)
      .attr('viewBox', '0 0 ' + W + ' ' + H)
      .attr('role', 'img')
      .attr('aria-label', 'Frequency response graph');
    var g = svg.append('g').attr('transform', 'translate(' + m.left + ',' + m.top + ')');

    /* ---- Figma 189:8257 paint: purple field, green area, glow ---- */
    var k = plotW / 1416;                       /* everything scales off the design width */
    var defs = svg.append('defs');
    var field = defs.append('radialGradient').attr('id', 'pkfr-field')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('cx', plotW * 0.772).attr('cy', plotH * 0.258).attr('r', plotW * 0.74);
    FIELD_STOPS.forEach(function (st) {
      field.append('stop').attr('offset', st[0]).attr('stop-color', st[1]);
    });
    var areaFill = defs.append('linearGradient').attr('id', 'pkfr-area')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0).attr('y1', plotH * 0.2).attr('x2', 0).attr('y2', plotH * 1.03);
    areaFill.append('stop').attr('offset', 0).attr('stop-color', AREA_TOP);
    areaFill.append('stop').attr('offset', 1).attr('stop-color', AREA_BOTTOM).attr('stop-opacity', 0.2);
    /* geometric guard: data is already trimmed to 20 Hz – 20 kHz, this catches
       what data cannot — Catmull-Rom overshoot at the two end samples and the
       stroke glow bleeding past the field */
    defs.append('clipPath').attr('id', 'pkfr-clip')
      .append('rect').attr('x', 0).attr('y', 0).attr('width', plotW).attr('height', plotH);
    defs.append('filter').attr('id', 'pkfr-glow')
      .attr('x', '-5%').attr('y', '-20%').attr('width', '110%').attr('height', '140%')
      .append('feDropShadow').attr('dx', 0).attr('dy', 0)
      .attr('stdDeviation', Math.max(3, 7 * k))
      .attr('flood-color', GREEN_GLOW).attr('flood-opacity', 1);

    g.append('rect').attr('x', 0).attr('y', 0).attr('width', plotW).attr('height', plotH)
      .attr('fill', 'url(#pkfr-field)');
    quintetArea = g.append('g').attr('clip-path', 'url(#pkfr-clip)')
      .append('path').attr('fill', 'url(#pkfr-area)').attr('stroke', 'none')
      .attr('d', areaGen()(MAIN));

    var minors = x.ticks(40).filter(function (v) { return v >= 20 && v <= 20000; });
    g.selectAll('.grid-v').data(minors).enter().append('line')
      .attr('x1', function (d) { return x(d); }).attr('x2', function (d) { return x(d); })
      .attr('y1', 0).attr('y2', plotH)
      .attr('stroke', function (d) { return MAJOR.indexOf(d) > -1 ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)'; })
      .attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
    [80, 70, 60, 50].forEach(function (v) {
      g.append('line').attr('x1', 0).attr('x2', plotW).attr('y1', y(v)).attr('y2', y(v))
        .attr('stroke', 'rgba(0,0,0,0.2)').attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
    });
    [0, plotH].forEach(function (yy) {
      g.append('line').attr('x1', 0).attr('x2', plotW).attr('y1', yy).attr('y2', yy)
        .attr('stroke', '#000').attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
    });
    g.append('line').attr('x1', 0).attr('x2', 0).attr('y1', 0).attr('y2', plotH)
      .attr('stroke', '#D7DDF3').attr('stroke-width', 1);

    [80, 70, 60, 50].forEach(function (v) {
      svg.append('text').attr('x', m.left - 8).attr('y', m.top + y(v) + 4)
        .attr('text-anchor', 'end').attr('font-family', FONT_SANS)
        .attr('font-size', isSmall ? 10 : 14).attr('font-weight', 600).attr('fill', '#000')
        .text(v + 'dB');
    });
    /* Hz labels: phones use the trimmed X_TICKS_SMALL set at 10px; whatever is
       drawn then gets a collision sweep so nothing can ever overlap (Rene
       2026-08-19). The two endpoints always survive. */
    var hzLabels = (isSmall ? X_TICKS_SMALL : X_TICKS).map(function (v) {
      return {
        v: v,
        node: svg.append('text').attr('x', m.left + x(v)).attr('y', H - 8)
          .attr('text-anchor', v === 20 ? 'start' : v === 20000 ? 'end' : 'middle')
          .attr('font-family', FONT_SANS).attr('font-size', isSmall ? 10 : 14)
          .attr('font-weight', 600).attr('fill', '#000')
          .text(fmtHz(v, isSmall)).node()
      };
    });
    (function cullHz() {
      var PAD = isSmall ? 2 : 6;
      var boxes = hzLabels.map(function (l) {
        var b = l.node.getBBox();
        return { node: l.node, x0: b.x, x1: b.x + b.width };
      });
      if (!boxes.length) return;
      var last = boxes[boxes.length - 1];
      var prev = boxes[0];
      for (var i = 1; i < boxes.length - 1; i++) {
        var b = boxes[i];
        if (b.x0 < prev.x1 + PAD || b.x1 + PAD > last.x0) b.node.remove();
        else prev = b;
      }
    })();

    /* band divisions — white so they read as structure, not as grid */
    BANDS.forEach(function (b, i) {
      if (!i) return;
      g.append('line')
        .attr('x1', x(b.from)).attr('x2', x(b.from)).attr('y1', 0).attr('y2', plotH)
        .attr('stroke', 'rgba(255,255,255,0.34)').attr('stroke-width', 1)
        .attr('stroke-dasharray', '5 5').attr('shape-rendering', 'crispEdges');
    });

    var curveLayer = g.append('g').attr('clip-path', 'url(#pkfr-clip)');
    quintetPath = curveLayer.append('path').attr('fill', 'none')
      .attr('stroke', GREEN).attr('stroke-width', Math.max(2, 4 * k)).attr('stroke-linecap', 'round')
      .attr('filter', 'url(#pkfr-glow)')
      .attr('d', lineGen()(MAIN));

    var dash = Math.max(2, 3 * k);
    compareDash = dash + ' ' + dash;
    comparePath = curveLayer.append('path').attr('fill', 'none')
      .attr('stroke', COMPARE_YELLOW).attr('stroke-width', Math.max(1.8, 5 * k))
      .attr('stroke-dasharray', compareDash).attr('opacity', 0);
    if (compare) comparePath.attr('d', lineGen()(compare.pts)).attr('opacity', 1);

    var len = quintetPath.node().getTotalLength();
    if (!reduceMotion && !drawn && window.gsap && window.ScrollTrigger) {
      quintetPath.attr('stroke-dasharray', len).attr('stroke-dashoffset', len);
      window.gsap.fromTo(quintetArea.node(), { opacity: 0 }, {
        opacity: 1, duration: 1.2, ease: 'power2.out',
        scrollTrigger: { trigger: mount, start: 'top 78%', once: true }
      });
      window.gsap.to(quintetPath.node(), {
        strokeDashoffset: 0, duration: 1.6, ease: 'power2.inOut',
        scrollTrigger: { trigger: mount, start: 'top 78%', once: true },
        onComplete: function () { drawn = true; quintetPath.attr('stroke-dasharray', null); }
      });
    } else { drawn = true; }

    /* hover crosshair + readout (both curves) */
    var hoverLayer = g.append('g').style('display', 'none');
    hoverLayer.append('line').attr('y1', 0).attr('y2', plotH).attr('stroke', 'rgba(255,255,255,0.5)').attr('stroke-dasharray', '3,3');
    var dotQ = hoverLayer.append('circle').attr('r', 4).attr('fill', GREEN).attr('stroke', '#0B0A1E').attr('stroke-width', 1.5);
    var dotC = hoverLayer.append('circle').attr('r', 4).attr('fill', COMPARE_YELLOW).attr('stroke', '#0B0A1E').attr('stroke-width', 1.5).style('display', 'none');
    var tip = hoverLayer.append('text').attr('font-family', FONT_MONO).attr('font-size', 12)
      .attr('fill', '#FFFFFF').attr('stroke', 'rgba(11,10,30,0.65)').attr('stroke-width', 3)
      .attr('paint-order', 'stroke').attr('stroke-linejoin', 'round');
    var bisect = d3.bisector(function (d) { return d[0]; }).left;
    function sample(pts, f) {
      var i = Math.min(Math.max(bisect(pts, f), 1), pts.length - 1);
      var a = pts[i - 1], b = pts[i];
      var t = (Math.log(f) - Math.log(a[0])) / (Math.log(b[0]) - Math.log(a[0]));
      return a[1] + t * (b[1] - a[1]);
    }
    svg.append('rect')
      .attr('x', m.left).attr('y', m.top).attr('width', plotW).attr('height', plotH)
      .attr('fill', 'transparent')
      .on('mouseenter', function () { hoverLayer.style('display', null); })
      .on('mouseleave', function () { hoverLayer.style('display', 'none'); })
      .on('mousemove', function (ev) {
        var px = d3.pointer(ev, this)[0] - m.left;
        var f = Math.min(20000, Math.max(20, x.invert(px)));
        var qv = sample(MAIN, f);
        hoverLayer.select('line').attr('x1', px).attr('x2', px);
        dotQ.attr('cx', px).attr('cy', y(qv));
        var lbl = (f >= 1000 ? (f / 1000).toFixed(1) + 'kHz' : Math.round(f) + 'Hz') + ' · ' + qv.toFixed(1) + 'dB';
        if (compare) {
          var cv = sample(compare.pts, f);
          dotC.style('display', null).attr('cx', px).attr('cy', y(cv));
          lbl += ' / ' + cv.toFixed(1) + 'dB';
        } else dotC.style('display', 'none');
        tip.attr('x', Math.min(px + 10, plotW - 190)).attr('y', Math.max(y(qv) - 12, 14)).text(lbl);
      });

    buildBands(m, isSmall);
  }

  /* ---- band strip: labels along the top of the plot, each with a tooltip.
     Appended to the SVG last so it sits above the crosshair hit rect; the
     highlight wash is pointer-events:none so the readout still works. ---- */
  function buildBands(m, isSmall) {
    var stripH = isSmall ? 19 : 28;
    var layer = svg.append('g').attr('transform', 'translate(' + m.left + ',' + m.top + ')');
    var card = document.createElement('div');
    card.className = 'pk-fr-band-tip' + (isSmall ? ' is-sm' : '');
    card.hidden = true;
    mount.appendChild(card);

    var items = [], open = -1;
    function close() {
      if (open < 0) return;
      items[open].wash.attr('opacity', 0);
      items[open].label.attr('fill', 'rgba(255,255,255,0.6)');
      card.hidden = true;
      open = -1;
    }
    function show(i) {
      if (open === i) return;
      close();
      open = i;
      var it = items[i];
      it.wash.attr('opacity', 0.09);
      it.label.attr('fill', '#FFFFFF');
      card.innerHTML = '';
      var h = document.createElement('b'); h.textContent = it.band.name;
      var r = document.createElement('i'); r.textContent = it.band.range;
      var t = document.createElement('span'); t.textContent = it.band.blurb;
      card.appendChild(h); card.appendChild(r); card.appendChild(t);
      card.hidden = false;
      /* centre on the band, then keep the card inside the chart */
      var half = card.offsetWidth / 2;
      var cx = m.left + (it.x0 + it.x1) / 2;
      card.style.left = Math.round(Math.min(Math.max(cx - half, 4), mount.clientWidth - card.offsetWidth - 4)) + 'px';
      /* phones park it low so it never hides the curve you just tapped */
      card.style.top = Math.round(isSmall
        ? m.top + plotH - card.offsetHeight - 8
        : m.top + stripH + 8) + 'px';
      if (window.gsap && !reduceMotion) {
        window.gsap.fromTo(card, { opacity: 0, y: -4 }, { opacity: 1, y: 0, duration: 0.18, ease: 'power2.out' });
      }
    }

    BANDS.forEach(function (b, i) {
      var x0 = x(b.from), x1 = x(b.to), w = x1 - x0;
      var wash = layer.append('rect').attr('x', x0).attr('y', 0).attr('width', w).attr('height', plotH)
        .attr('fill', '#FFFFFF').attr('opacity', 0).style('pointer-events', 'none');
      var label = layer.append('text')
        .attr('x', x0 + w / 2).attr('y', stripH * 0.66).attr('text-anchor', 'middle')
        .attr('font-family', FONT_SANS).attr('font-size', isSmall ? 8 : 11)
        .attr('font-weight', 600).attr('letter-spacing', isSmall ? '0' : '0.08em')
        .attr('fill', 'rgba(255,255,255,0.6)').style('pointer-events', 'none')
        .text(b.name.toUpperCase());
      /* full name → short name → nothing, whichever first clears the band width */
      var room = w - (isSmall ? 4 : 10);
      if (label.node().getComputedTextLength() > room) label.text(b.short.toUpperCase());
      if (label.node().getComputedTextLength() > room) label.text('');
      var hit = layer.append('rect').attr('x', x0).attr('y', 0).attr('width', w).attr('height', stripH)
        .attr('fill', 'transparent').style('cursor', 'help')
        .attr('tabindex', 0).attr('role', 'button')
        .attr('aria-label', b.name + ', ' + b.range + '. ' + b.blurb);
      items.push({ band: b, wash: wash, label: label, x0: x0, x1: x1 });
      hit.on('pointerenter', function (ev) { if (ev.pointerType !== 'touch') show(i); })
        .on('pointerleave', function (ev) { if (ev.pointerType !== 'touch') close(); })
        .on('click', function (ev) { ev.stopPropagation(); open === i ? close() : show(i); })
        .on('focus', function () { show(i); })
        .on('blur', close)
        .on('keydown', function (ev) { if (ev.key === 'Escape') close(); });
    });
    /* one outside-click listener at a time — build() re-runs on every resize */
    if (bandAway) document.removeEventListener('click', bandAway);
    bandAway = close;
    document.addEventListener('click', bandAway);
  }

  function setCompare(name, pts) {
    var cpts = pts ? (clampCurve(pts) || pts) : null;
    if (cpts) cpts = smooth(cpts);
    compare = cpts ? { name: name, pts: normalise(cpts) } : null;
    var swatch = document.getElementById('pk-fr-swatch');
    var label = document.getElementById('pk-fr-comparelabel');
    if (swatch) {
      swatch.style.backgroundColor = compare ? COMPARE_YELLOW : SWATCH_IDLE;
      /* Figma 189:8263 rims the yellow chip so it reads on white */
      swatch.style.boxShadow = compare ? 'inset 0 0 0 1px rgba(0,0,0,0.26)' : '';
    }
    if (label) label.textContent = compare ? compare.name : 'Select IEM to Compare';
    if (!comparePath) return;
    if (compare) {
      comparePath.attr('d', lineGen()(compare.pts));
      if (reduceMotion) { comparePath.attr('opacity', 1); }
      else {
        var len = comparePath.node().getTotalLength();
        comparePath.attr('opacity', 1).attr('stroke-dasharray', len).attr('stroke-dashoffset', len)
          .transition().duration(900).ease(d3.easeCubicInOut).attr('stroke-dashoffset', 0)
          .on('end', function () { comparePath.attr('stroke-dasharray', compareDash).attr('stroke-dashoffset', null); });
      }
    } else {
      comparePath.transition().duration(250).attr('opacity', 0);
    }
  }

  /* ---- searchable compare picker ---- */
  (function () {
    var btn = document.getElementById('pk-fr-comparebtn');
    var panel = document.getElementById('pk-fr-panel');
    var input = document.getElementById('pk-fr-search');
    var list = document.getElementById('pk-fr-results');
    if (!btn || !panel || !input || !list) return;
    var ownHandle = btn.dataset.handle || '';
    var debounce;

    function open() {
      panel.classList.remove('hidden');
      btn.setAttribute('aria-expanded', 'true');
      input.value = ''; render([]);
      input.focus();
      if (window.gsap && !reduceMotion) window.gsap.fromTo(panel, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: .18, ease: 'power2.out' });
    }
    function close() {
      panel.classList.add('hidden');
      btn.setAttribute('aria-expanded', 'false');
    }
    btn.addEventListener('click', function (e) { e.preventDefault(); panel.classList.contains('hidden') ? open() : close(); });
    document.addEventListener('click', function (e) { if (!panel.contains(e.target) && !btn.contains(e.target)) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

    function row(text, cls) {
      var li = document.createElement('li');
      li.className = cls || '';
      li.textContent = text;
      return li;
    }
    function render(items, note) {
      list.innerHTML = '';
      if (compare) {
        var clear = row('✕ Clear comparison', 'pk-fr-item pk-fr-clear');
        clear.addEventListener('click', function () { setCompare(null, null); close(); });
        list.appendChild(clear);
      }
      if (note) list.appendChild(row(note, 'pk-fr-note'));
      items.forEach(function (p) {
        var li = row(p.title, 'pk-fr-item');
        li.addEventListener('click', function () {
          li.textContent = p.title + ' …';
          fetch('/products/' + p.handle + '?view=frdata')
            .then(function (r) { return r.json(); })
            .then(function (d) {
              var pts = (d && Array.isArray(d.fr) && d.fr.length > 2) ? d.fr : parseCurveText(d && d.text);
              if (pts && pts.length > 2) { setCompare((d && d.title) || p.title, pts); close(); }
              else { li.textContent = p.title; render(items, 'No tuning data for “' + p.title + '” yet.'); }
            })
            .catch(function () { li.textContent = p.title; render(items, 'Could not load tuning data.'); });
        });
        list.appendChild(li);
      });
      if (!items.length && !note && input.value.length > 1) list.appendChild(row('No products found.', 'pk-fr-note'));
    }
    input.addEventListener('input', function () {
      clearTimeout(debounce);
      var q = input.value.trim();
      if (q.length < 2) { render([]); return; }
      debounce = setTimeout(function () {
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6')
          .then(function (r) { return r.json(); })
          .then(function (d) {
            var items = (((d || {}).resources || {}).results || {}).products || [];
            items = items.filter(function (p) { return p.handle !== ownHandle; });
            render(items);
          })
          .catch(function () { render([], 'Search unavailable.'); });
      }, 250);
    });
  })();

  build();
  var t;
  if ('ResizeObserver' in window) new ResizeObserver(function () { clearTimeout(t); t = setTimeout(build, 150); }).observe(mount);
  else window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(build, 150); });
})();
